import { useCallback, useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import {
  createHelpTicket,
  createTicketResponse,
  getHelpTickets,
  type HelpTicket as Ticket,
  type TicketCategory,
  type TicketPriority,
} from '../../services/helpService';
import storageService from '../../services/storageService';
import type { ResponseFormData, TicketFormData } from './types';

const EMPTY_ARRAY: never[] = [];

export const useHelpTickets = (activeTab: string) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [responseLoading, setResponseLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [readTickets, setReadTickets] = useState<string[]>([]);
  const { account: user } = useAccount();
  const { showSnackbar } = useSnackbar();

  const [ticketForm, setTicketForm] = useState<TicketFormData>({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
    attachments: [],
  });

  const [responseForm, setResponseForm] = useState<ResponseFormData>({
    ticketId: '',
    message: '',
  });

  const loadTickets = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getHelpTickets();
      setTickets(response.tickets || EMPTY_ARRAY);
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(`Failed to load tickets: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    if (activeTab === 'tickets' && user) {
      loadTickets();
    }
  }, [activeTab, user, loadTickets]);

  const handleTicketClick = async (ticketId: string): Promise<void> => {
    if (!readTickets.includes(ticketId)) {
      setReadTickets([...readTickets, ticketId]);
    }
    try {
      const response = await getHelpTickets({ ticketId });
      setSelectedTicket(response.ticket || null);
      setOpenDialog(true);
      setResponseForm({ ticketId, message: '' });
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(`Failed to load ticket details: ${err.message}`, 'error');
    }
  };

  const handleAddResponse = async (): Promise<void> => {
    if (!responseForm.message.trim()) {
      showSnackbar('Please enter a response message', 'error');
      return;
    }
    setResponseLoading(true);
    try {
      await createTicketResponse(responseForm.ticketId, responseForm.message.trim());
      showSnackbar('Response added successfully!', 'success');

      const response = await getHelpTickets({ ticketId: responseForm.ticketId });
      setSelectedTicket(response.ticket || null);
      setResponseForm({ ...responseForm, message: '' });

      loadTickets();
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(err.message, 'error');
    } finally {
      setResponseLoading(false);
    }
  };

  const submitNewTicket = async (
    e: React.FormEvent,
    setActiveTab: (tab: string) => void
  ): Promise<void> => {
    e.preventDefault();

    if (!ticketForm.subject.trim() || !ticketForm.description.trim()) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const uploadedAttachments: { filename: string; url: string }[] = [];

      if (ticketForm.attachments && ticketForm.attachments.length > 0) {
        for (const attachment of ticketForm.attachments) {
          const filePath = `support_tickets/${user?.uid || 'unknown_user'}/${Date.now()}_${attachment.file.name}`;
          const uploadResult = await storageService.uploadFile(filePath, attachment.file);
          if (uploadResult.success && uploadResult.downloadURL) {
            uploadedAttachments.push({
              filename: attachment.file.name,
              url: uploadResult.downloadURL,
            });
          } else {
            throw new Error(`Failed to upload attachment: ${attachment.file.name}`);
          }
        }
      }

      await createHelpTicket({
        subject: ticketForm.subject.trim(),
        description: ticketForm.description.trim(),
        category: ticketForm.category as TicketCategory,
        priority: ticketForm.priority as TicketPriority,
        ...(uploadedAttachments.length > 0 && { attachments: uploadedAttachments }),
      });

      showSnackbar(`Ticket created successfully!`, 'success');
      setTicketForm({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium',
        attachments: [],
      });

      if (activeTab === 'tickets') {
        loadTickets();
      } else {
        setActiveTab('tickets');
      }
    } catch (error: unknown) {
      const err = error as Error;
      showSnackbar(`Failed to create ticket: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return {
    tickets,
    loading,
    responseLoading,
    openDialog,
    setOpenDialog,
    selectedTicket,
    readTickets,
    ticketForm,
    setTicketForm,
    responseForm,
    setResponseForm,
    handleTicketClick,
    handleAddResponse,
    submitNewTicket,
  };
};
